'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { Tag, Gauge, ShieldCheck, CalendarCheck, Sparkles } from 'lucide-react';
import { useLocale } from '@/lib/i18n';

interface PromotionalBannerProps {
  onClaimDiscount: () => void;
}

export function PromotionalBanner({ onClaimDiscount }: PromotionalBannerProps) {
  const { t } = useLocale();
  const [bgError, setBgError] = useState(false);

  const pillars = [
    {
      icon: Tag,
      title: t('promo.1t'),
      desc: t('promo.1d'),
    },
    {
      icon: Gauge,
      title: t('promo.2t'),
      desc: t('promo.2d'),
    },
    {
      icon: ShieldCheck,
      title: t('promo.3t'),
      desc: t('promo.3d'),
    },
    {
      icon: CalendarCheck,
      title: t('promo.4t'),
      desc: t('promo.4d'),
    },
  ];

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-8 lg:py-12">
      <div className="relative rounded-3xl overflow-hidden bg-[#11161f] border border-slate-800 shadow-2xl p-8 sm:p-12 lg:p-14">
        {/* Real Photographic Background (Road Trip Open Air Adventure) */}
        {!bgError && (
          <div className="absolute inset-0 z-0">
            <Image
              src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&q=80"
              alt="Road Trip Adventure"
              fill
              className="object-cover object-center opacity-25 filter brightness-75 contrast-125"
              referrerPolicy="no-referrer"
              onError={() => setBgError(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#11161f] via-[#11161f]/90 to-[#11161f]/80" />
          </div>
        )}

        {/* Ambient Glow */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left Column: Heading, Subhead & CTA (Span 5) */}
          <div className="lg:col-span-5 flex flex-col items-start">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-wider mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              <span>{t('promo.badge')}</span>
            </div>

            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight mb-3">
              {t('promo.titleA')} <br />
              <span className="text-[var(--color-primary)]">{t('promo.titleB')}</span>
            </h2>

            <p className="text-sm text-slate-300 leading-relaxed mb-8 max-w-md">
              {t('promo.sub')} <strong className="text-amber-400 font-mono">FLEETAVA20</strong> {t('promo.at')}
            </p>

            <button
              type="button"
              onClick={onClaimDiscount}
              className="px-8 py-3.5 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-95 text-white font-extrabold text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-orange-950/40 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('promo.cta')}
            </button>
          </div>

          {/* Right Column: 4 Pillars with Warm Orange Badges (Span 7) */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
            {pillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <div
                  key={idx}
                  className="flex flex-col items-start p-4 rounded-2xl bg-white/[0.04] backdrop-blur-xs border border-white/10 hover:border-amber-500/30 transition-all"
                >
                  <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-[var(--color-primary)]" />
                  </div>
                  <h3 className="text-sm font-bold text-white tracking-tight mb-1">
                    {pillar.title}
                  </h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    {pillar.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
